import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import PlanCards from "../components/PlanCards";
import "../components/Billing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = { free: "Complimentary Account", parish_pro: "Parish Pro", cathedral: "Cathedral Edition", diocese: "Diocese Edition" };
const PRO_FEATURES = ["Unlimited SundayBuilder™", "Parish Memory™", "Parish Intelligence™", "AI Reports"];

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function fmtWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const now = new Date();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return { day: "Today", time };
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return { day: "Yesterday", time };
  return { day: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), time };
}

export default function Account() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [organization, setOrganization] = useState(user?.organization || "");
  const [saved, setSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportInfo, setExportInfo] = useState(null);
  const [exportErr, setExportErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/parish/overview`).then((r) => setOverview(r.data)).catch(() => setOverview(null));
  }, []);

  const createExport = async () => {
    setExportBusy(true); setExportErr(""); setExportInfo(null); setCopied(false);
    try {
      const { data } = await axios.post(`${API}/parish/export`);
      setExportInfo(data);
      setOverview((o) => o ? { ...o, security: { ...o.security, last_export: new Date().toISOString() } } : o);
    } catch (e) {
      setExportErr(e.response?.data?.detail || "Could not create the export. Please try again.");
    } finally { setExportBusy(false); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(exportInfo.passphrase); setCopied(true); } catch { /* ignore */ }
  };

  const ent = user?.entitlements || {};
  const planKey = ent.plan || "free";
  const isActive = ent.plan_status === "active";
  const isPaid = planKey !== "free" && isActive;

  const parishName = overview?.parish?.name || organization || "My Parish";
  const established = fmtDate(overview?.parish?.established);
  const snap = overview?.snapshot;
  const sec = overview?.security;
  const health = overview?.health;
  const lastLogin = fmtWhen(sec?.last_login);
  const lastExport = fmtWhen(sec?.last_export);
  const lastGenerated = fmtWhen(snap?.last_generated);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true); setSaved(false);
    try {
      const { data } = await axios.patch(`${API}/auth/profile`, { name, organization });
      setUser(data); setSaved(true);
    } catch { /* ignore */ } finally { setSavingProfile(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/portal`, { plan: "_", origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not open the billing portal.");
      setPortalBusy(false);
    }
  };

  return (
    <div className="acct" data-testid="account-page">
      <div className="acct-top">
        <div className="b1">Liturgical <span>Intelligence™</span></div>
        <button className="acct-back" onClick={() => navigate("/dashboard")} data-testid="acct-back">← Dashboard</button>
      </div>

      <div className="acct-body">
        <div className="acct-eyebrow">Parish Administration Center</div>

        {/* ───────── Parish header ───────── */}
        <div className="padm-hero" data-testid="parish-admin-hero">
          <div className="padm-hero-main">
            <h1 className="acct-h1" style={{ margin: 0 }} data-testid="parish-hero-name">{parishName}</h1>
            <div className="padm-hero-tags">
              <span className="plan-pill">{PLAN_LABEL[planKey] || "Free"}</span>
              {established && <span className="padm-tag">Established {established}</span>}
            </div>
          </div>
          <div className="padm-hero-meta">
            {overview?.identity?.rector && (
              <div className="padm-meta-item"><label>Owner</label><span>{overview.identity.rector}</span></div>
            )}
            {overview?.identity?.diocese && (
              <div className="padm-meta-item"><label>Diocese</label><span>{overview.identity.diocese}</span></div>
            )}
            {overview?.identity?.denomination && !overview?.identity?.diocese && (
              <div className="padm-meta-item"><label>Tradition</label><span>{overview.identity.denomination}</span></div>
            )}
            <div className="padm-meta-item">
              <label>Subscription</label>
              <span className={isActive ? "padm-ok" : ""}>{isActive ? "Active" : (ent.plan_status || "Inactive")}</span>
            </div>
          </div>
        </div>

        {/* ───────── Parish Snapshot ───────── */}
        <div className="acct-card" data-testid="parish-snapshot-card">
          <h2>Parish Snapshot</h2>
          <div className="padm-snapshot">
            <div className="padm-stat" data-testid="snapshot-memory">
              <div className="padm-stat-n">{snap ? snap.memory_records : "—"}</div>
              <div className="padm-stat-l">Parish Memory™<br />records</div>
            </div>
            <div className="padm-stat" data-testid="snapshot-plans">
              <div className="padm-stat-n">{snap ? snap.worship_plans : "—"}</div>
              <div className="padm-stat-l">SundayBuilder™<br />plans</div>
            </div>
            <div className="padm-stat" data-testid="snapshot-reports">
              <div className="padm-stat-n">{snap ? snap.reports : "—"}</div>
              <div className="padm-stat-l">Parish Intelligence™<br />reports</div>
            </div>
            <div className="padm-stat" data-testid="snapshot-last-generated">
              <div className="padm-stat-n small">{lastGenerated ? lastGenerated.day : "—"}</div>
              <div className="padm-stat-l">Last<br />generated</div>
            </div>
            <div className="padm-stat" data-testid="snapshot-users">
              <div className="padm-stat-n">{snap ? snap.users : "—"}</div>
              <div className="padm-stat-l">Parish<br />users</div>
            </div>
          </div>
        </div>

        {/* ───────── Profile ───────── */}
        <div className="acct-card" data-testid="profile-card">
          <h2>Profile</h2>
          <form onSubmit={saveProfile}>
            <div className="acct-field">
              <label>Full name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>
            <div className="acct-field">
              <label>Organization / Parish</label>
              <input value={organization} onChange={(e) => setOrganization(e.target.value)}
                placeholder="e.g. St. Mary's Episcopal Church" data-testid="profile-org" />
            </div>
            <div className="acct-field">
              <label>Email</label>
              <input value={user?.email || ""} disabled data-testid="profile-email" />
            </div>
            <button className="acct-save" type="submit" disabled={savingProfile} data-testid="profile-save">
              {savingProfile ? "Saving…" : "Save Changes"}
            </button>
            {saved && <span className="acct-saved" data-testid="profile-saved">Saved ✓</span>}
          </form>
        </div>

        {/* ───────── Subscription ───────── */}
        <div className="acct-card" data-testid="plan-card">
          <h2>Subscription</h2>
          <div className="padm-sub-head">
            <span className="plan-pill" data-testid="current-plan">{PLAN_LABEL[planKey] || "Free"}</span>
            <span className={`plan-badge${isActive ? " active" : ""}`}>{isActive ? "✓ Active" : ent.plan_status || "Inactive"}</span>
          </div>
          {isPaid ? (
            <>
              <ul className="padm-sub-features" data-testid="plan-features">
                {PRO_FEATURES.map((f) => <li key={f}><span className="padm-check">✓</span>{f}</li>)}
              </ul>
              <div className="padm-sub-renewal">
                <label>Renewal</label>
                <span>Renews automatically</span>
              </div>
            </>
          ) : (
            <div className="plan-meta" data-testid="entitlement-meta">
              {ent.onboarding?.sundaybuilder_used ? "Complimentary plan used · 0 reports remaining" : "Complimentary onboarding available"}
              {ent.single_report_credits > 0 && ` · ${ent.single_report_credits} SundayBuilder™ credit(s)`}
            </div>
          )}
          {ent.stripe_customer_id && (
            <button className="acct-portal" onClick={openPortal} disabled={portalBusy} data-testid="manage-billing">
              {portalBusy ? "Opening…" : "Manage Subscription"}
            </button>
          )}
        </div>

        {/* ───────── Parish Data & Security ───────── */}
        <div className="acct-card" data-testid="data-export-card">
          <h2>Parish Data & Security</h2>
          <p className="acct-sub" style={{ marginBottom: 18 }}>
            <strong>Your parish always owns its data.</strong> Export a complete encrypted archive of
            your worship plans, reports, Parish Memory™, and institutional knowledge at any time.
          </p>

          <div className="padm-trust" data-testid="security-trust-indicators">
            <div className="padm-trust-item" data-testid="trust-last-export">
              <label>Last Export</label>
              {lastExport ? <span>{lastExport.day}<em>{lastExport.time}</em></span> : <span>Never</span>}
            </div>
            <div className="padm-trust-item" data-testid="trust-last-login">
              <label>Last Login</label>
              {lastLogin ? <span>{lastLogin.day}<em>{lastLogin.time}</em></span> : <span>—</span>}
            </div>
            <div className="padm-trust-item" data-testid="trust-encryption">
              <label>Encryption</label>
              <span className="padm-ok">AES-256 Enabled</span>
            </div>
          </div>

          {!exportInfo && (
            <button className="acct-save" onClick={createExport} disabled={exportBusy}
              data-testid="create-export-btn">
              {exportBusy ? "Preparing encrypted archive…" : "Create Encrypted Export"}
            </button>
          )}
          {exportErr && <p style={{ color: "#a33", marginTop: 10 }} data-testid="export-error">{exportErr}</p>}
          {exportInfo && (
            <div className="padm-export" data-testid="export-ready">
              <h3>Archive Encryption</h3>
              <p className="padm-export-note">This archive is protected with a one-time recovery key.</p>
              <label className="padm-key-label">Recovery Key</label>
              <div className="padm-key-row">
                <code data-testid="export-passphrase">{exportInfo.passphrase}</code>
                <button type="button" className="padm-copy" onClick={copyKey} data-testid="copy-passphrase-btn">
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <p className="padm-key-warning" data-testid="key-warning">
                ⚠ Save this key securely. It cannot be recovered later.
              </p>
              <a className="acct-save padm-download" href={`${API}/parish/export/download?token=${exportInfo.download_token}`}
                data-testid="download-export-btn">
                Download Parish Archive
              </a>
              <p className="padm-download-meta" data-testid="export-meta">
                ZIP Archive · AES-256 Encrypted · {Math.max(1, Math.round(exportInfo.size_bytes / 1024))} KB ·
                one-time download, expires in {exportInfo.expires_in_minutes} minutes
              </p>
            </div>
          )}
        </div>

        {/* ───────── Platform Health ───────── */}
        <div className="acct-card" data-testid="platform-health-card">
          <h2>Platform Health</h2>
          <ul className="padm-health">
            <li data-testid="health-memory"><span className={health?.parish_memory_active ? "padm-check" : "padm-dot"}>{health?.parish_memory_active ? "✓" : "○"}</span>Parish Memory™ {health?.parish_memory_active ? "Active" : "Awaiting first records"}</li>
            <li data-testid="health-ai"><span className={health?.ai_services_online ? "padm-check" : "padm-dot"}>{health?.ai_services_online ? "✓" : "○"}</span>AI Services {health?.ai_services_online ? "Online" : "Offline"}</li>
            <li data-testid="health-encryption"><span className="padm-check">✓</span>Encryption Enabled</li>
            <li data-testid="health-notifications"><span className="padm-check">✓</span>Notifications Active</li>
            <li data-testid="health-email"><span className={health?.email_delivery ? "padm-check" : "padm-dot"}>{health?.email_delivery ? "✓" : "○"}</span>Email Delivery {health?.email_delivery ? "Configured" : "Not configured"}</li>
          </ul>
        </div>

        {/* ───────── Data & Privacy ───────── */}
        <div className="acct-card" data-testid="data-privacy-card">
          <h2>Data &amp; Privacy</h2>
          <p style={{ lineHeight: 1.6, color: "#3a4458", marginBottom: 14 }}>
            You own your ministry data. Export everything, request a correction, or delete your account
            with a 30-day grace period. No support ticket required.
          </p>
          <button
            className="acct-save"
            onClick={() => navigate("/account/data-privacy")}
            data-testid="open-data-privacy-btn"
          >
            Open Data &amp; Privacy Center
          </button>
        </div>

        {/* ───────── Plans & Upgrades ───────── */}
        <div className="acct-card" data-testid="upgrade-card">
          <h2>Plans & Upgrades</h2>
          {isPaid && (
            <>
              <div className="padm-current-plan" data-testid="current-plan-block">
                <label>Current Plan</label>
                <div>
                  <span className="plan-pill">{PLAN_LABEL[planKey]}</span>
                  <span className="plan-badge active">ACTIVE</span>
                </div>
              </div>
              <div className="padm-divider"><span>Other Plans</span></div>
            </>
          )}
          <PlanCards currentPlan={isActive ? planKey : null} hideCurrent={isPaid} />
        </div>
      </div>
    </div>
  );
}
